/**
 * Generato automaticamente.
 * framework-cmp-domande
 */
import { useQuery } from 'react-query';
import type {
  QueryFunction,
  QueryKey,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { apiClientRequest } from '../../../client/apiClient';
import type { ErrorType } from '../../../client/apiClient';
import type {
  GetFrontRisorseCondiviseIdMetadatiDoc200,
  GetFrontRisorseCondiviseMetadatiDoc200,
  GetFrontRisorseCondiviseMetadatiDocParams,
} from '.././schemas';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P,
) => any
  ? P
  : never;

export const getFrontRisorseCondiviseIdMetadatiDoc = (
  maskedId: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<GetFrontRisorseCondiviseIdMetadatiDoc200>(
    {
      url: `/framework-cmp-domande/risorse-condivise/${maskedId}/metadati-doc`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetFrontRisorseCondiviseIdMetadatiDocQueryKey = (
  maskedId: string,
) =>
  [
    `/framework-cmp-domande/risorse-condivise/${maskedId}/metadati-doc`,
  ] as const;

export const getGetFrontRisorseCondiviseIdMetadatiDocQueryOptions = <
  TData = Awaited<ReturnType<typeof getFrontRisorseCondiviseIdMetadatiDoc>>,
  TError = ErrorType<unknown>,
>(
  maskedId: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontRisorseCondiviseIdMetadatiDoc>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getFrontRisorseCondiviseIdMetadatiDoc>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetFrontRisorseCondiviseIdMetadatiDocQueryKey(maskedId);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getFrontRisorseCondiviseIdMetadatiDoc>>
  > = ({ signal }) =>
    getFrontRisorseCondiviseIdMetadatiDoc(maskedId, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!maskedId, ...queryOptions };
};

export type GetFrontRisorseCondiviseIdMetadatiDocQueryResult = NonNullable<
  Awaited<ReturnType<typeof getFrontRisorseCondiviseIdMetadatiDoc>>
>;
export type GetFrontRisorseCondiviseIdMetadatiDocQueryError =
  ErrorType<unknown>;

export const useGetFrontRisorseCondiviseIdMetadatiDoc = <
  TData = Awaited<ReturnType<typeof getFrontRisorseCondiviseIdMetadatiDoc>>,
  TError = ErrorType<unknown>,
>(
  maskedId: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontRisorseCondiviseIdMetadatiDoc>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetFrontRisorseCondiviseIdMetadatiDocQueryOptions(
    maskedId,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const getFrontRisorseCondiviseIdDownloadFileId = (
  maskedId: string,
  fileId: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<void>(
    {
      url: `/framework-cmp-domande/risorse-condivise/${maskedId}/download-file/${fileId}`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetFrontRisorseCondiviseIdDownloadFileIdQueryKey = (
  maskedId: string,
  fileId: string,
) =>
  [
    `/framework-cmp-domande/risorse-condivise/${maskedId}/download-file/${fileId}`,
  ] as const;

export const getGetFrontRisorseCondiviseIdDownloadFileIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getFrontRisorseCondiviseIdDownloadFileId>>,
  TError = ErrorType<unknown>,
>(
  maskedId: string,
  fileId: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontRisorseCondiviseIdDownloadFileId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getFrontRisorseCondiviseIdDownloadFileId>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetFrontRisorseCondiviseIdDownloadFileIdQueryKey(maskedId, fileId);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getFrontRisorseCondiviseIdDownloadFileId>>
  > = ({ signal }) =>
    getFrontRisorseCondiviseIdDownloadFileId(
      maskedId,
      fileId,
      requestOptions,
      signal,
    );

  return {
    queryKey,
    queryFn,
    enabled: !!(maskedId && fileId),
    ...queryOptions,
  };
};

export type GetFrontRisorseCondiviseIdDownloadFileIdQueryResult = NonNullable<
  Awaited<ReturnType<typeof getFrontRisorseCondiviseIdDownloadFileId>>
>;
export type GetFrontRisorseCondiviseIdDownloadFileIdQueryError =
  ErrorType<unknown>;

export const useGetFrontRisorseCondiviseIdDownloadFileId = <
  TData = Awaited<ReturnType<typeof getFrontRisorseCondiviseIdDownloadFileId>>,
  TError = ErrorType<unknown>,
>(
  maskedId: string,
  fileId: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontRisorseCondiviseIdDownloadFileId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetFrontRisorseCondiviseIdDownloadFileIdQueryOptions(
    maskedId,
    fileId,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const getFrontRisorseCondiviseMetadatiDoc = (
  params: GetFrontRisorseCondiviseMetadatiDocParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<GetFrontRisorseCondiviseMetadatiDoc200>(
    {
      url: `/framework-cmp-domande/risorse-condivise/metadati-doc`,
      method: 'get',
      params,
      signal,
    },
    options,
  );
};

export const getGetFrontRisorseCondiviseMetadatiDocQueryKey = (
  params: GetFrontRisorseCondiviseMetadatiDocParams,
) =>
  [
    `/framework-cmp-domande/risorse-condivise/metadati-doc`,
    ...(params ? [params] : []),
  ] as const;

export const getGetFrontRisorseCondiviseMetadatiDocQueryOptions = <
  TData = Awaited<ReturnType<typeof getFrontRisorseCondiviseMetadatiDoc>>,
  TError = ErrorType<unknown>,
>(
  params: GetFrontRisorseCondiviseMetadatiDocParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontRisorseCondiviseMetadatiDoc>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getFrontRisorseCondiviseMetadatiDoc>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetFrontRisorseCondiviseMetadatiDocQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getFrontRisorseCondiviseMetadatiDoc>>
  > = ({ signal }) =>
    getFrontRisorseCondiviseMetadatiDoc(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetFrontRisorseCondiviseMetadatiDocQueryResult = NonNullable<
  Awaited<ReturnType<typeof getFrontRisorseCondiviseMetadatiDoc>>
>;
export type GetFrontRisorseCondiviseMetadatiDocQueryError = ErrorType<unknown>;

export const useGetFrontRisorseCondiviseMetadatiDoc = <
  TData = Awaited<ReturnType<typeof getFrontRisorseCondiviseMetadatiDoc>>,
  TError = ErrorType<unknown>,
>(
  params: GetFrontRisorseCondiviseMetadatiDocParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontRisorseCondiviseMetadatiDoc>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetFrontRisorseCondiviseMetadatiDocQueryOptions(
    params,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};
